import { datadogLogs } from "@datadog/browser-logs";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  FulfillmentGroupData,
  FulfillmentGroupPickStatus,
  getNumUnpickedItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OpenLikeStatuses } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { memo, ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as styles from "./fulfillment-group-actions.module.css";

export const ActionButtonMessage = memo(function ActionButtonMessage({
  errorMessage,
  insufficientFunds,
  warningMessage,
  anyGroupPending,
  fulfillmentGroups,
  clearBuyLabelErrorsOnRender = false,
}: {
  errorMessage: string | null;
  insufficientFunds: boolean;
  warningMessage: string;
  anyGroupPending: boolean;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  clearBuyLabelErrorsOnRender?: boolean;
}) {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const rpcClient = useContext(RedoOutboundLabelsRpcClientContext);
  const acknowledgedBuyLabelErrorKeys = useRef(new Set<string>());

  const wmsEnabled = team?.settings?.outboundLabels?.wms?.enabled ?? false;
  const hasOneProblemOrder =
    fulfillmentGroups.length === 1 &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    fulfillmentGroups[0].pickStatus === FulfillmentGroupPickStatus.PROBLEM;
  const numUnpickedItems = fulfillmentGroups[0]
    ? getNumUnpickedItems(fulfillmentGroups[0], {
        hideInputItems:
          team?.settings?.outboundLabels?.billOfMaterials
            ?.alwaysHideInputItems ?? false,
      })
    : null;
  const rendersProblemOrderMessage =
    wmsEnabled &&
    hasOneProblemOrder &&
    !!numUnpickedItems &&
    numUnpickedItems > 0;

  const buyLabelErrorGroupIds = useMemo(
    () =>
      fulfillmentGroups
        .filter(
          (group) =>
            OpenLikeStatuses.includes(group.status) &&
            !!group.buyLabelErrors?.length,
        )
        .map((group) => group._id),
    [fulfillmentGroups],
  );
  const buyLabelErrorGroupKey = buyLabelErrorGroupIds.join(",");

  const errorCounts = new Map<string, number>();

  for (const group of fulfillmentGroups) {
    if (OpenLikeStatuses.includes(group.status)) {
      const groupErrors = new Set<string>();

      for (const e of group.buyLabelErrors || []) {
        groupErrors.add(e.message);
      }
      for (const e of group.buyReturnLabelErrors || []) {
        groupErrors.add(e.message);
      }

      for (const message of groupErrors) {
        errorCounts.set(message, (errorCounts.get(message) || 0) + 1);
      }
    }
  }

  const uniqueBuyTimeErrors: string[] = Array.from(errorCounts.entries()).map(
    ([message, count]) => {
      if (count > 1) {
        return `${message} (${count} occurrences)`;
      }
      return message;
    },
  );

  const nonBuyTimeErrors: ReactNode[] = [];
  if (insufficientFunds) {
    nonBuyTimeErrors.push(
      <Text
        cursor="pointer"
        fontSize="xs"
        hoverTextColor="primary"
        onClick={() => {
          navigate(`/stores/${team?._id}/settings/billing/outbound/balance`);
        }}
        textColor="secondary"
        textDecoration="underline"
      >
        Add funds to your account to purchase labels.
      </Text>,
    );
  }
  if (errorMessage) {
    nonBuyTimeErrors.push(
      <ErrorMessageTemplate key="error-message" message={errorMessage} />,
    );
  }
  if (warningMessage) {
    nonBuyTimeErrors.push(
      <ErrorMessageTemplate key="warning-message" message={warningMessage} />,
    );
  }

  const allErrors = filterTruthy([...uniqueBuyTimeErrors, ...nonBuyTimeErrors]);

  useEffect(() => {
    if (
      !clearBuyLabelErrorsOnRender ||
      !rpcClient ||
      rendersProblemOrderMessage ||
      anyGroupPending ||
      !uniqueBuyTimeErrors.length ||
      !buyLabelErrorGroupIds.length ||
      acknowledgedBuyLabelErrorKeys.current.has(buyLabelErrorGroupKey)
    ) {
      return;
    }

    acknowledgedBuyLabelErrorKeys.current.add(buyLabelErrorGroupKey);
    void rpcClient
      .acknowledgeBuyLabelErrors({ fulfillmentGroupIds: buyLabelErrorGroupIds })
      .catch((error) => {
        acknowledgedBuyLabelErrorKeys.current.delete(buyLabelErrorGroupKey);
        datadogLogs.logger.warn("Failed to acknowledge buy label errors", {
          error,
          fulfillmentGroupIds: buyLabelErrorGroupIds,
        });
      });
  }, [
    anyGroupPending,
    buyLabelErrorGroupIds,
    buyLabelErrorGroupKey,
    clearBuyLabelErrorsOnRender,
    rendersProblemOrderMessage,
    rpcClient,
    uniqueBuyTimeErrors.length,
  ]);

  if (rendersProblemOrderMessage) {
    const msg =
      numUnpickedItems === 1
        ? "1 unpicked item"
        : `${numUnpickedItems} unpicked items`;
    return <ErrorMessageTemplate key="error-message" message={msg} />;
  }

  if (!allErrors.length || anyGroupPending) {
    return null;
  }

  return (
    <Flex className={styles.errorsContainer} dir="column" gap="sm">
      {uniqueBuyTimeErrors.length > 0 && (
        <Text fontSize="xs" textColor="error">
          {uniqueBuyTimeErrors.length > 1
            ? "We encountered the following errors while purchasing labels:"
            : "We encountered the following error while purchasing labels:"}
        </Text>
      )}
      {uniqueBuyTimeErrors.map((error, index) => (
        <ErrorMessageTemplate key={`buy-time-error-${index}`} message={error} />
      ))}
      {uniqueBuyTimeErrors.length > 0 && nonBuyTimeErrors.length > 0 && (
        <Text fontSize="xs" pt="lg" textColor="error">
          The following errors are unrelated to label purchases:
        </Text>
      )}
      {nonBuyTimeErrors}
    </Flex>
  );
});

const ErrorMessageTemplate = memo(function ErrorMessageTemplate({
  message,
}: {
  message: string;
}) {
  return (
    <Flex dir="row" gap="xs" key="error-message">
      <Text fontSize="xs" textColor="error">
        •
      </Text>
      <Text fontSize="xs" textColor="error">
        {message}
      </Text>
    </Flex>
  );
});
